import { useEffect, useRef, useState } from "react";

const MAX_PULL = 90;
const IGNITION_THRESHOLD = 40;

export const LampScene = ({ isOn, onToggle }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [dragging, setDragging] = useState(false);
  const draggingRef = useRef(false);
  const startYRef = useRef(0);
  const pointerIdRef = useRef(null);
  const keyboardTimerRef = useRef(null);
  const thresholdTriggeredRef = useRef(false);

  useEffect(() => () => window.clearTimeout(keyboardTimerRef.current), []);

  const springBack = (target, pointerId) => {
    if (pointerId !== undefined && target?.hasPointerCapture?.(pointerId)) {
      target.releasePointerCapture(pointerId);
    }
    pointerIdRef.current = null;
    draggingRef.current = false;
    setDragging(false);
    setPullDistance(0);
  };

  const handlePointerDown = (event) => {
    event.preventDefault();
    pointerIdRef.current = event.pointerId;
    startYRef.current = event.clientY;
    thresholdTriggeredRef.current = false;
    draggingRef.current = true;
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!draggingRef.current || pointerIdRef.current !== event.pointerId) return;
    const nextDistance = Math.min(MAX_PULL, Math.max(0, event.clientY - startYRef.current));
    setPullDistance(nextDistance);
    if (nextDistance >= IGNITION_THRESHOLD && !thresholdTriggeredRef.current) {
      thresholdTriggeredRef.current = true;
      onToggle();
    }
  };

  const handlePointerEnd = (event) => springBack(event.currentTarget, event.pointerId);

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    window.clearTimeout(keyboardTimerRef.current);
    draggingRef.current = true;
    setDragging(true);
    setPullDistance(54);
    onToggle();
    keyboardTimerRef.current = window.setTimeout(() => {
      draggingRef.current = false;
      setDragging(false);
      setPullDistance(0);
    }, 140);
  };

  const pullStyle = {
    "--pull-distance": `${pullDistance}px`,
    "--cord-transition": dragging ? "none" : "height 680ms cubic-bezier(0.175,0.885,0.32,1.275)",
    "--knob-transition": dragging ? "none" : "transform 680ms cubic-bezier(0.175,0.885,0.32,1.275)",
  };

  return (
    <div className="lamp-stage" data-testid="lamp-container">
      <div className="lamp-floor-glow" aria-hidden="true" />
      <div className="lamp-light-cone" data-testid="lamp-light-cone" aria-hidden="true" />

      <div className="lamp-assembly">
        <div className="lamp-shade" data-testid="lamp-shade" aria-hidden="true">
          <div className="lamp-shade-highlight" />
          <div className="lamp-shade-rim" />
        </div>

        <div className="lamp-bulb" data-testid="lamp-bulb" aria-hidden="true">
          <span className="lamp-filament" />
        </div>
        <div className="lamp-socket" aria-hidden="true" />
        <div className="lamp-stem" data-testid="lamp-stem" aria-hidden="true" />
        <div className="lamp-base" data-testid="lamp-base" aria-hidden="true">
          <span />
        </div>

        <div className="lamp-cord-system" style={pullStyle} data-testid="lamp-pull-cord">
          <span className="lamp-cord-anchor" aria-hidden="true" />
          <span className="lamp-cord-line" aria-hidden="true" />
          <button type="button" className="lamp-pull-knob" data-testid="lamp-pull-knob"
            aria-label="Pull lamp cord to switch on light and reveal login form" aria-pressed={isOn}
            onPointerDown={handlePointerDown} onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd} onPointerCancel={handlePointerEnd}
            onKeyDown={handleKeyDown}>
            <span className="lamp-knob-ring" aria-hidden="true" />
            <span className="lamp-knob-weight" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};